import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBedsPaginated, useCreateBed, useUpdateBed, useDeleteBed, useBulkDeleteBeds, useWards } from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import {
  Bed,
  Plus,
  Edit2,
  X,
  Loader2,
  AlertCircle,
  Building2,
  Tag,
  Activity,
  Banknote,
  FileText,
  Power,
  MapPin,
  Stethoscope,
  Users,
  Baby,
  HeartPulse,
  Zap,
  CheckCircle2,
  Clock,
  Wrench,
  Search,
  SlidersHorizontal,
  Trash2,
  Layers,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { BedDetailsModal } from '../../components/ui/BedDetailsModal';
import { FacilityTabs } from '../../components/clinical/FacilityTabs';
import { toast } from 'sonner';

const bedTypeOptions = ['GENERAL', 'SEMI_PRIVATE', 'PRIVATE', 'ICU', 'CCU', 'NICU', 'EMERGENCY'] as const;
const bedStatusOptions = ['AVAILABLE', 'OCCUPIED', 'RESERVED', 'MAINTENANCE'] as const;

const typeLabels: Record<string, string> = {
  GENERAL: 'General',
  SEMI_PRIVATE: 'Semi-Private (Cabin)',
  PRIVATE: 'Private (Cabin)',
  ICU: 'ICU',
  CCU: 'CCU',
  NICU: 'NICU',
  EMERGENCY: 'Emergency',
};

const typeIcons: Record<string, React.ElementType> = {
  GENERAL: Bed,
  SEMI_PRIVATE: Users,
  PRIVATE: Bed,
  ICU: Stethoscope,
  CCU: HeartPulse,
  NICU: Baby,
  EMERGENCY: Zap,
};

const typeColors: Record<string, string> = {
  GENERAL: 'bg-slate-500/10 text-slate-600 border-slate-200',
  SEMI_PRIVATE: 'bg-blue-500/10 text-blue-600 border-blue-200',
  PRIVATE: 'bg-indigo-500/10 text-indigo-600 border-indigo-200',
  ICU: 'bg-rose-500/10 text-rose-600 border-rose-200',
  CCU: 'bg-orange-500/10 text-orange-600 border-orange-200',
  NICU: 'bg-pink-500/10 text-pink-600 border-pink-200',
  EMERGENCY: 'bg-red-500/10 text-red-600 border-red-200',
};



const statusLabels: Record<string, string> = {
  AVAILABLE: 'Available',
  OCCUPIED: 'Occupied',
  RESERVED: 'Reserved',
  MAINTENANCE: 'Maintenance',
};

const statusIcons: Record<string, React.ElementType> = {
  AVAILABLE: CheckCircle2,
  OCCUPIED: Bed,
  RESERVED: Clock,
  MAINTENANCE: Wrench,
};

const statusColors: Record<string, string> = {
  AVAILABLE: 'bg-success/10 text-success',
  OCCUPIED: 'bg-danger/10 text-danger',
  RESERVED: 'bg-warning/10 text-warning',
  MAINTENANCE: 'bg-text-muted/10 text-text-muted',
};

const statusBadgeVariants: Record<string, any> = {
  AVAILABLE: 'success',
  OCCUPIED: 'danger',
  RESERVED: 'warning',
  MAINTENANCE: 'default',
};

const statusActiveColors: Record<string, string> = {
  AVAILABLE: 'border-success bg-success/5 ring-2 ring-success/20',
  OCCUPIED: 'border-danger bg-danger/5 ring-2 ring-danger/20',
  RESERVED: 'border-warning bg-warning/5 ring-2 ring-warning/20',
  MAINTENANCE: 'border-text-muted bg-text-muted/5 ring-2 ring-text-muted/20',
};

interface FormErrors {
  bedNumber?: string;
  wardId?: string;
  type?: string;
  price?: string;
}

type BedTab = 'all' | 'available' | 'occupied' | 'reserved' | 'maintenance';

// Tabs are driven by the `?tab=` URL param so a view can be bookmarked/shared.
const BED_TABS: { key: BedTab; label: string; icon: React.ElementType }[] = [
  { key: 'all', label: 'All', icon: Layers },
  { key: 'available', label: 'Available', icon: CheckCircle2 },
  { key: 'occupied', label: 'Occupied', icon: Bed },
  { key: 'reserved', label: 'Reserved', icon: Clock },
  { key: 'maintenance', label: 'Maintenance', icon: Wrench },
];

const BED_TAB_STATUS: Record<BedTab, string> = {
  all: '',
  available: 'AVAILABLE',
  occupied: 'OCCUPIED',
  reserved: 'RESERVED',
  maintenance: 'MAINTENANCE',
};

const BED_STATUS_TAB: Record<string, BedTab> = {
  '': 'all',
  AVAILABLE: 'available',
  OCCUPIED: 'occupied',
  RESERVED: 'reserved',
  MAINTENANCE: 'maintenance',
};

export function Beds() {
  const limit = 20;

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [viewBed, setViewBed] = useState<any>(null);
  const [form, setForm] = useState({
    bedNumber: '',
    ward: '',
    wardCode: '',
    wardId: '',
    type: 'GENERAL' as any,
    status: 'AVAILABLE' as any,
    price: 0,
    description: '',
    isActive: true,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // List controls
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: BedTab = tabParam && tabParam in BED_TAB_STATUS ? (tabParam as BedTab) : 'all';
  const statusFilter = BED_TAB_STATUS[tab];

  /** Keeps the active tab in the URL so it can be bookmarked/shared. */
  const setTab = (next: BedTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [typeFilter, setTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const queryFilters = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      status: statusFilter || undefined,
      type: typeFilter || undefined,
    }),
    [debouncedSearch, statusFilter, typeFilter],
  );
  const activeFilterCount = (statusFilter ? 1 : 0) + (typeFilter ? 1 : 0);

  const { data: bedsData, isLoading, error } = useBedsPaginated(page, limit, queryFilters);
  const { data: wardsData } = useWards(1, 100);
  const createBed = useCreateBed();
  const updateBed = useUpdateBed();
  const deleteBed = useDeleteBed();
  const bulkDeleteBed = useBulkDeleteBeds();

  const beds = useMemo(() => bedsData?.data || [], [bedsData]);
  const meta = bedsData?.meta;
  const wards = useMemo(() => wardsData?.data || [], [wardsData]);

  // Reset to first page whenever the query changes.
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, typeFilter]);

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(beds.map((bed: any) => bed.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [beds]);

  const allSelected = beds.length > 0 && beds.every((bed: any) => selectedIds.has(bed.id));
  const someSelected = beds.some((bed: any) => selectedIds.has(bed.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = beds.length > 0 && beds.every((bed: any) => prev.has(bed.id));
      return everySelected ? new Set() : new Set(beds.map((bed: any) => bed.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteBed.mutateAsync(deleteTarget.id);
      toast.success(`Deleted ${deleteTarget.bedNumber}`);
    } catch {
      toast.error('Failed to delete bed');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      await bulkDeleteBed.mutateAsync(ids);
      setSelectedIds(new Set());
      toast.success(`Deleted ${ids.length} bed${ids.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to delete some beds');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  const selectedWard = useMemo(() => wards.find((w: any) => w.id === form.wardId), [wards, form.wardId]);

  const validate = (): boolean => {
    const nextErrors: FormErrors = {};
    if (!form.bedNumber.trim()) nextErrors.bedNumber = 'Bed number is required';
    else if (form.bedNumber.trim().length < 2) nextErrors.bedNumber = 'Bed number must be at least 2 characters';
    if (!form.wardId) nextErrors.wardId = 'Ward is required';
    if (form.price < 0) nextErrors.price = 'Price cannot be negative';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  useEffect(() => {
    if (Object.keys(touched).length > 0) {
      validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const resetForm = () => {
    setForm({ bedNumber: '', ward: '', wardCode: '', wardId: '', type: 'GENERAL', status: 'AVAILABLE', price: 0, description: '', isActive: true });
    setErrors({});
    setTouched({});
    setShowForm(false);
    setEditing(null);
  };

  const startEdit = (b: any) => {
    setEditing(b);
    setForm({
      bedNumber: b.bedNumber,
      ward: b.ward,
      wardCode: b.wardCode || '',
      wardId: b.wardId || '',
      type: b.type,
      status: b.status,
      price: b.price || 0,
      description: b.description || '',
      isActive: b.isActive,
    });
    setErrors({});
    setTouched({});
    setShowForm(true);
  };

  const handleEditFromDetails = (b: any) => {
    setViewBed(null);
    startEdit(b);
  };

  const handleWardChange = (wardId: string) => {
    const selected = wards.find((w: any) => w.id === wardId);
    const wardBedType = selected?.wardType && (bedTypeOptions as readonly string[]).includes(selected.wardType)
      ? selected.wardType
      : undefined;
    setForm((prev) => ({
      ...prev,
      wardId,
      ward: selected ? selected.wardName : prev.ward,
      wardCode: selected ? selected.wardCode : prev.wardCode,
      type: wardBedType || prev.type,
    }));
    setTouched((prev) => ({ ...prev, wardId: true }));
  };

  const updateField = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ bedNumber: true, wardId: true, type: true, price: true });
    if (!validate()) return;

    if (editing) {
      updateBed.mutate({ id: editing.id, data: form }, { onSuccess: resetForm });
    } else {
      createBed.mutate(form, { onSuccess: resetForm });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bed className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">Beds</h1>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          Add Bed
        </button>
      </div>

      {/* Tabs: route tabs left, status filter tabs right */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Route tabs (Wards / Beds) */}
        <FacilityTabs />

        {/* Status tabs */}
        <div className="flex w-fit flex-wrap items-center gap-1 rounded-xl border border-border bg-surface p-1">
          {BED_TABS.map(({ key, label, icon: Icon }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-text-muted hover:bg-background hover:text-text'
                }`}
              >
                <Icon size={16} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load beds</span>
        </div>
      )}

      {/* Search + Filter Bar */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by bed number, ward..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`px-3 py-2 border rounded-lg text-sm font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${
              showFilters || activeFilterCount > 0
                ? 'border-primary text-primary bg-primary/5'
                : 'border-border text-text-muted hover:text-text hover:bg-background'
            }`}
          >
            <SlidersHorizontal size={14} />
            Filter
            {activeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-semibold">
                {activeFilterCount}
              </span>
            )}
          </button>
          {(search || activeFilterCount > 0) && (
            <button
              type="button"
              onClick={() => { setSearch(''); setTab('all'); setTypeFilter(''); setPage(1); }}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setTab(BED_STATUS_TAB[e.target.value] ?? 'all')}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">All statuses</option>
                {bedStatusOptions.map((s) => (
                  <option key={s} value={s}>{statusLabels[s]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Bed Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">All types</option>
                {bedTypeOptions.map((t) => (
                  <option key={t} value={t}>{typeLabels[t]}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteBed.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteBed.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteBed.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={beds.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Bed</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Ward</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {beds.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                      {search || activeFilterCount > 0 ? (
                        <span>
                          No beds match your filters.{' '}
                          <button
                            onClick={() => { setSearch(''); setTab('all'); setTypeFilter(''); setPage(1); }}
                            className="text-primary hover:underline"
                          >
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        'No beds configured. Add your first bed to get started.'
                      )}
                    </td>
                  </tr>
                ) : (
                  beds.map((bed: any) => (
                    <tr key={bed.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(bed.id)}
                          onChange={() => toggleOne(bed.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <Bed size={14} className="text-primary" />
                          </div>
                          <div>
                            <button
                              type="button"
                              onClick={() => setViewBed(bed)}
                              className="text-sm font-medium text-text hover:text-primary text-left transition-colors"
                              title="View bed details"
                            >
                              {bed.bedNumber}
                            </button>
                            {bed.description && <p className="text-xs text-text-muted">{bed.description}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text">{bed.ward}</td>
                      <td className="px-4 py-3 text-sm text-text">{typeLabels[bed.type] || bed.type}</td>
                      <td className="px-4 py-3 text-sm text-text">৳{bed.price || 0}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusColors[bed.status] || 'bg-text-muted/10 text-text-muted'}`}>
                          {bed.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => startEdit(bed)}
                            className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-primary transition-colors"
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(bed)}
                            className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-danger transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="beds"
          />
        </div>
      )}

      {/* Advanced Bed Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Bed size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-text">{editing ? 'Edit Bed' : 'New Bed'}</h2>
                  <p className="text-xs text-text-muted">
                    {editing ? 'Update bed details and configuration' : 'Configure a new bed for patient admission'}
                  </p>
                </div>
              </div>
              <button onClick={resetForm} className="p-2 hover:bg-background rounded-lg transition-colors">
                <X size={18} className="text-text-muted" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
                {/* LEFT: Form Fields */}
                <div className="lg:col-span-3 space-y-5">
                  {/* Bed Identification */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Tag size={14} className="text-primary" />
                      <h3 className="text-sm font-semibold text-text">Bed Identification</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input
                        label="Bed Number *"
                        placeholder="e.g. W-A-101"
                        value={form.bedNumber}
                        onChange={(e) => updateField('bedNumber', e.target.value)}
                        error={touched.bedNumber ? errors.bedNumber : undefined}
                      />
                      <div>
                        <label className="block text-sm font-medium text-text mb-1">Ward *</label>
                        <select
                          value={form.wardId}
                          onChange={(e) => handleWardChange(e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                            touched.wardId && errors.wardId ? 'border-danger' : 'border-border'
                          }`}
                        >
                          <option value="">Select a ward</option>
                          {wards.map((w: any) => (
                            <option key={w.id} value={w.id}>
                              {w.wardName} {w.wardCode ? `(${w.wardCode})` : ''}
                            </option>
                          ))}
                        </select>
                        {touched.wardId && errors.wardId && (
                          <p className="mt-1 text-xs text-danger">{errors.wardId}</p>
                        )}
                      </div>
                    </div>
                    {selectedWard && (
                      <div className="flex items-center gap-2 p-2.5 bg-background rounded-lg border border-border">
                        <MapPin size={14} className="text-text-muted" />
                        <span className="text-xs text-text-muted">Ward Code:</span>
                        <Badge variant="secondary">{selectedWard.wardCode || 'N/A'}</Badge>
                        <span className="text-xs text-text-muted ml-2">Floor:</span>
                        <Badge variant="secondary">{selectedWard.floor || 'N/A'}</Badge>
                      </div>
                    )}
                  </div>

                  {/* Bed Configuration */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Activity size={14} className="text-primary" />
                      <h3 className="text-sm font-semibold text-text">Bed Configuration</h3>
                    </div>

                    {/* Type — derived from Ward */}
                    <div>
                      <label className="block text-sm font-medium text-text mb-2">Bed Type</label>
                      <div className="flex items-center gap-3 p-3 bg-background rounded-xl border border-border">
                        {(() => {
                          const TIcon = typeIcons[form.type] || Bed;
                          return (
                            <div className={`p-2 rounded-lg ${typeColors[form.type] || 'bg-slate-500/10 text-slate-600'}`}>
                              <TIcon size={18} />
                            </div>
                          );
                        })()}
                        <div>
                          <p className="text-sm font-medium text-text">{typeLabels[form.type] || form.type}</p>
                          <p className="text-xs text-text-muted">Inherited from selected ward</p>
                        </div>
                      </div>
                    </div>

                    {/* Status Selector */}
                    <div>
                      <label className="block text-sm font-medium text-text mb-2">Status</label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {bedStatusOptions.map((s) => {
                          const Icon = statusIcons[s];
                          const isSelected = form.status === s;
                          return (
                            <button
                              key={s}
                              type="button"
                              onClick={() => updateField('status', s)}
                              className={`flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all ${
                                isSelected
                                  ? statusActiveColors[s]
                                  : 'border-border bg-surface hover:bg-background'
                              }`}
                            >
                              <Icon size={16} className={isSelected ? 'text-text' : 'text-text-muted'} />
                              <span className={`text-xs font-medium ${isSelected ? 'text-text' : 'text-text-muted'}`}>
                                {statusLabels[s]}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Pricing & Details */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Banknote size={14} className="text-primary" />
                      <h3 className="text-sm font-semibold text-text">Pricing & Details</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text mb-1">Daily Price (৳)</label>
                        <div className="relative">
                          <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                          <ValidatedNumberInput
                            value={form.price}
                            onChange={(v) => updateField('price', v)}
                            placeholder="0.00"
                            className={`w-full pl-10 pr-4 py-2 border rounded-lg bg-surface text-text text-sm placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                              touched.price && errors.price ? 'border-danger focus:ring-danger/20' : 'border-border'
                            }`}
                          />
                        </div>
                        {touched.price && errors.price && <p className="mt-1 text-xs text-danger">{errors.price}</p>}
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-3 p-3 bg-background rounded-lg border border-border cursor-pointer select-none w-full">
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={form.isActive}
                              onChange={(e) => updateField('isActive', e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-10 h-5 bg-text-muted/20 rounded-full peer-checked:bg-primary transition-colors" />
                            <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
                          </div>
                          <div>
                            <span className="text-sm font-medium text-text">{form.isActive ? 'Active' : 'Inactive'}</span>
                            <p className="text-xs text-text-muted">
                              {form.isActive ? 'Bed is available for use' : 'Bed is disabled'}
                            </p>
                          </div>
                          <Power size={16} className={`ml-auto ${form.isActive ? 'text-success' : 'text-text-muted'}`} />
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text mb-1">Description</label>
                      <textarea
                        value={form.description}
                        onChange={(e) => updateField('description', e.target.value)}
                        placeholder="Optional notes about the bed, equipment, or special features..."
                        rows={2}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-surface text-text text-sm placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* RIGHT: Live Preview */}
                <div className="lg:col-span-2">
                  <div className="sticky top-0 space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <FileText size={16} className="text-primary" />
                          Preview
                        </CardTitle>
                        <CardDescription>How this bed will appear in the system</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Bed Card Preview */}
                        <div className="p-4 bg-background rounded-xl border border-border space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <Bed size={18} className="text-primary" />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-text">
                                  {form.bedNumber || '—'}
                                </p>
                                {form.description ? (
                                  <p className="text-xs text-text-muted line-clamp-1">{form.description}</p>
                                ) : (
                                  <p className="text-xs text-text-muted">No description</p>
                                )}
                              </div>
                            </div>
                            <Badge variant={statusBadgeVariants[form.status] || 'default'}>
                              {statusLabels[form.status]}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
                            <div className="p-2 rounded-lg bg-surface">
                              <p className="text-[10px] text-text-muted uppercase tracking-wide">Ward</p>
                              <p className="text-sm font-medium text-text truncate">
                                {selectedWard?.wardName || '—'}
                              </p>
                            </div>
                            <div className="p-2 rounded-lg bg-surface">
                              <p className="text-[10px] text-text-muted uppercase tracking-wide">Type</p>
                              <p className="text-sm font-medium text-text">{typeLabels[form.type]}</p>
                            </div>
                            <div className="p-2 rounded-lg bg-surface">
                              <p className="text-[10px] text-text-muted uppercase tracking-wide">Daily Price</p>
                              <p className="text-sm font-medium text-text">৳{Number(form.price).toFixed(2)}</p>
                            </div>
                            <div className="p-2 rounded-lg bg-surface">
                              <p className="text-[10px] text-text-muted uppercase tracking-wide">State</p>
                              <p className={`text-sm font-medium ${form.isActive ? 'text-success' : 'text-text-muted'}`}>
                                {form.isActive ? 'Active' : 'Inactive'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Ward Info */}
                        {selectedWard && (
                          <div className="p-3 bg-background rounded-lg border border-border flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <Building2 size={14} className="text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs text-text-muted">Assigned Ward</p>
                              <p className="text-sm font-medium text-text truncate">{selectedWard.wardName}</p>
                              <p className="text-xs text-text-muted truncate">
                                Code: {selectedWard.wardCode || 'N/A'} • Floor: {selectedWard.floor || 'N/A'}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Quick Tips */}
                        {!editing && (
                          <div className="p-3 bg-info/5 border border-info/10 rounded-lg">
                            <p className="text-xs font-medium text-info mb-1">Tip</p>
                            <p className="text-xs text-text-muted">
                              Choose a descriptive bed number like "ICU-01" or "W-A-101" for easy identification.
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Actions */}
                    <Card>
                      <CardContent className="pt-4 space-y-3">
                        {(createBed.isError || updateBed.isError) && (
                          <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-danger text-sm">
                            <AlertCircle size={16} className="shrink-0 mt-0.5" />
                            <span>
                              {(createBed.error as any)?.response?.data?.message ||
                                (updateBed.error as any)?.response?.data?.message ||
                                'Something went wrong. Please check your inputs.'}
                            </span>
                          </div>
                        )}
                        <Button
                          type="submit"
                          variant="primary"
                          isLoading={createBed.isPending || updateBed.isPending}
                          disabled={createBed.isPending || updateBed.isPending}
                          className="w-full"
                        >
                          {editing ? 'Update Bed' : 'Create Bed'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          onClick={resetForm}
                        >
                          Cancel
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <BedDetailsModal
        bed={viewBed}
        onClose={() => setViewBed(null)}
        onEdit={handleEditFromDetails}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Delete "${deleteTarget?.bedNumber || ''}"?`}
        description="This action will remove the selected bed. This cannot be undone."
        confirmLabel="Delete bed"
        loading={deleteBed.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} bed${selectedIds.size === 1 ? '' : 's'}?`}
        description="This action will remove the selected beds. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size} bed${selectedIds.size === 1 ? '' : 's'}`}
        loading={bulkDeleteBed.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
